<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import type { DevLink, DevLinkStatus } from "@/backend/read/devLinks";
import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import { dropdownKey } from "@/components/ui/DropdownMenu/injectKeys";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import { injectStrict } from "@/utils/vue";

const props = defineProps<{ devLinks: DevLink[]; loading: boolean }>();

const { t } = useI18n();
const dropdown = injectStrict(dropdownKey);

const statusLabels: Record<DevLinkStatus, string> = {
  OPEN: t("devLinks.status.open"),
  MERGED: t("devLinks.status.merged"),
  DECLINED: t("devLinks.status.declined"),
};

interface RepoGroup {
  name: string;
  url?: string;
  links: DevLink[];
}

function sanitizeUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "https:" || parsed.protocol === "http:") return parsed.href;
  } catch {
    // invalid URL
  }
  return undefined;
}

const groupedLinks = computed(() => {
  const groups = new Map<string, RepoGroup>();
  for (const link of props.devLinks) {
    const name = link.repositoryName ?? t("devLinks.unknownRepo");
    const group = groups.get(name);
    if (group) {
      group.links.push(link);
    } else {
      groups.set(name, { name, url: sanitizeUrl(link.repositoryUrl), links: [link] });
    }
  }
  return [...groups.values()];
});

function openLink(url: string) {
  window.open(url, "_blank", "noopener,noreferrer");
  dropdown.close();
}
</script>

<template>
  <MenuList role="dialog" class="dev-links-menu">
    <MenuListItems role="menu">
      <MenuListItem v-if="props.loading" :static="true">
        <span class="empty-state">{{ t("devLinks.loading") }}</span>
      </MenuListItem>
      <MenuListItem v-else-if="props.devLinks.length === 0" :static="true">
        <span class="empty-state">{{ t("devLinks.empty") }}</span>
      </MenuListItem>
      <template v-else>
        <template v-for="group in groupedLinks" :key="group.name">
          <div class="repo-header">
            <a
              v-if="group.url"
              :href="group.url"
              target="_blank"
              rel="noopener noreferrer"
              class="repo-badge repo-badge--link"
            >
              {{ group.name }}
            </a>
            <span v-else class="repo-badge">{{ group.name }}</span>
          </div>
          <MenuListItem v-for="link in group.links" :key="link.id" @click="openLink(link.url)">
            <BaseTooltip :content="link.title" position="right" show-when-clipped>
              <div class="link-content">
                <span class="link-title">{{ link.title }}</span>
                <span :class="['link-status', link.devStatus.toLowerCase()]">
                  {{ statusLabels[link.devStatus] }}
                </span>
              </div>
            </BaseTooltip>
          </MenuListItem>
        </template>
      </template>
    </MenuListItems>
  </MenuList>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";
@use "@/styles/mixins/typography";
@use "@/styles/mixins/utils";

.dev-links-menu {
  min-width: 200px;
}

.repo-header {
  padding: 6px 12px 2px;
}

.repo-badge {
  @include typography.small("bold");

  display: inline-block;
  padding: 1px 8px;
  border-radius: 4px;
  background-color: colors.$background-grey;
  color: colors.$text-subtle;

  &--link {
    text-decoration: none;
    color: colors.$text-subtle;

    &:hover {
      background-color: colors.$alternative-blue-background-light;
      color: colors.$alternative-blue;
    }
  }
}

.empty-state {
  @include typography.medium("semi-bold");

  display: block;
  color: colors.$text-subtle;
  text-align: center;
}

.dev-links-menu :deep(.content .base-tooltip) {
  display: block;
  width: 100%;
}

.link-content {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
}

.link-title {
  @include utils.ellipsis;

  flex: 1;
}

.link-status {
  @include typography.small;

  flex-shrink: 0;
  padding: 1px 6px;
  border-radius: 4px;

  &.open {
    background-color: colors.$alternative-blue-background-light;
    color: colors.$alternative-blue;
  }

  &.merged {
    background-color: colors.$primary-success-light;
    color: colors.$primary-success;
  }

  &.declined {
    background-color: colors.$alternative-red-background-light;
    color: colors.$alternative-red;
  }
}
</style>
